import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (formData.name.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Please enter your project details");
      return;
    }
    if (formData.message.trim().length < 20) {
      toast.error("Please provide more details about your project (at least 20 characters)");
      return;
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/enquiries`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        company: formData.company.trim() || null,
        service: formData.service || null,
        message: formData.message.trim()
      });
      setIsSubmitted(true);
      toast.success("Enquiry submitted successfully! We'll get back to you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error("Failed to submit enquiry. Please try again or call us at (01) 630 1410.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Address",
      content: "Unit 4b, Canal Bank, Hume Ave, Park West Industrial Park, Dublin, D12 ARX3, Ireland"
    },
    {
      icon: Phone,
      title: "Phone",
      content: "+353 1 630 1410",
      link: "tel:+35316301410"
    },
    {
      icon: Mail,
      title: "Email",
      content: "info@nassauengineering.ie",
      link: "mailto:info@nassauengineering.ie"
    },
    {
      icon: Clock,
      title: "Business Hours",
      content: "Mon - Thurs: 8:00 AM - 4:30 PM, Fri: 8:00 AM - 3:30 PM"
    }
  ];

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1586333109867-812586269a58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzdGVlbCUyMGZhYnJpY2F0aW9uJTIwd2VsZGluZyUyMHNwYXJrcyUyMGluZHVzdHJpYWwlMjBmYWN0b3J5fGVufDB8fHx8MTc3MDA2MDIwNnww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Contact Us
            </p>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Get In <span className="text-blue-500">Touch</span>
            </h1>
            <p className="text-slate-300 text-lg max-w-3xl mx-auto">
              Feel free to contact us to discuss additional details of your project. 
              Our team is ready to get started with your project right away.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 
                className="text-2xl font-bold text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Send Us an <span className="text-blue-500">Enquiry</span>
              </h2>
              
              {isSubmitted ? (
                <Card className="bg-slate-900 border-slate-800 rounded-sm">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                    <h3 
                      className="text-xl font-bold text-white uppercase mb-4"
                      style={{ fontFamily: 'var(--font-heading)' }}
                    >
                      Thank You!
                    </h3>
                    <p className="text-slate-400 mb-6">
                      Your enquiry has been submitted successfully. Our team will get back to you within 24 hours.
                    </p>
                    <Button
                      data-testid="send-another-btn"
                      onClick={() => setIsSubmitted(false)}
                      className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider"
                    >
                      Send Another Enquiry
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-300">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Murphy"
                        className="bg-slate-900 border-slate-800 rounded-sm focus:border-blue-500 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-300">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.ie"
                        className="bg-slate-900 border-slate-800 rounded-sm focus:border-blue-500 text-white placeholder:text-slate-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-300">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="contact-phone-input"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+353 1 XXX XXXX"
                        className="bg-slate-900 border-slate-800 rounded-sm focus:border-blue-500 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-slate-300">
                        Company Name
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="contact-company-input"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Ltd"
                        className="bg-slate-900 border-slate-800 rounded-sm focus:border-blue-500 text-white placeholder:text-slate-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 relative z-20">
                    <Label htmlFor="service" className="text-slate-300">
                      Service Required
                    </Label>
                    <Select onValueChange={handleServiceChange} value={formData.service}>
                      <SelectTrigger 
                        data-testid="contact-service-select"
                        className="bg-slate-900 border-slate-800 rounded-sm focus:border-blue-500 text-white [&>span]:text-slate-400"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 z-50">
                        <SelectItem value="sheet-metal">Sheet Metal Fabrication</SelectItem>
                        <SelectItem value="laser-cutting">Laser Cutting</SelectItem>
                        <SelectItem value="bim-services">BIM Services</SelectItem>
                        <SelectItem value="pharmaceutical">Pharmaceutical Clean Rooms</SelectItem>
                        <SelectItem value="structural-steel">Structural Steel</SelectItem>
                        <SelectItem value="custom">Custom Fabrication</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-300">
                      Project Details *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Please describe your project requirements, timeline, and any specific details..."
                      className="bg-slate-900 border-slate-800 rounded-sm focus:border-blue-500 text-white placeholder:text-slate-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider btn-glow"
                  >
                    {isSubmitting ? (
                      "Submitting..."
                    ) : (
                      <>
                        Send Enquiry
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>

                  <p className="text-slate-500 text-sm text-center">
                    We offer free estimates within 24 hours (depending on project size)
                  </p>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 
                className="text-2xl font-bold text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Contact <span className="text-blue-500">Information</span>
              </h2>

              <div className="space-y-6 mb-12">
                {contactInfo.map((info, idx) => (
                  <Card 
                    key={idx}
                    data-testid={`contact-info-${idx}`}
                    className="bg-slate-900 border-slate-800 rounded-sm"
                  >
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-600/20 rounded-sm flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">{info.title}</h3>
                        {info.link ? (
                          <a 
                            href={info.link}
                            className="text-slate-400 hover:text-blue-400 transition-colors"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-slate-400">{info.content}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Map Embed */}
              <div className="rounded-sm overflow-hidden border border-slate-800">
                <iframe
                  data-testid="contact-map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2383.2947099561!2d-6.373832923478087!3d53.33662257228668!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48670c8e7f0a0a0f%3A0x8c0c0c0c0c0c0c0c!2sPark%20West%20Industrial%20Park%2C%20Dublin!5e0!3m2!1sen!2sie!4v1620000000000!5m2!1sen!2sie"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Nassau Engineering Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Response Banner */}
      <section className="py-12 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h3 
                className="text-xl font-bold text-white uppercase tracking-tight"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Need Immediate Assistance?
              </h3>
              <p className="text-blue-100">
                Call us directly for urgent enquiries
              </p>
            </div>
            <a href="tel:+35316301410">
              <Button 
                data-testid="call-now-btn"
                size="lg"
                className="bg-white text-blue-600 hover:bg-slate-100 rounded-sm uppercase tracking-wider"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Now: +353 1 630 1410
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
